import apiClient from "@/lib/apiClient";

export interface OwnerAnnouncement {
  id: number;
  title: string;
  summary: string;
  published_at: string;
  is_read: boolean;
}

export interface OwnerAnnouncementsResponse {
  announcements: OwnerAnnouncement[];
  unread_count: number;
}

export const fetchOwnerAnnouncements = async (): Promise<OwnerAnnouncementsResponse> => {
  const response = await apiClient.get<OwnerAnnouncementsResponse>("/dashboard/owner_announcements");
  return response.data;
};

export const markOwnerAnnouncementAsRead = async (id: number): Promise<void> => {
  await apiClient.put(`/dashboard/owner_announcements/${id}/read`);
};

export const markAllOwnerAnnouncementsAsRead = async (): Promise<void> => {
  await apiClient.put("/dashboard/owner_announcements/read_all");
};

// Owner情報取得
export const fetchMe = async (): Promise<any> => {
  const response = await apiClient.get("/dashboard/me");
  return response.data;
};

export interface SiteMember {
  id: number;
  email: string;
  role: "owner" | "member";
  status: "active" | "suspended";
  joined_at: string;
  last_sign_in_at?: string | null;
  otp_enabled: boolean;
}

export interface SiteInvitation {
  id: number;
  email: string;
  status: "pending";
  expires_at: string;
  created_at?: string;
}

export interface SiteMembersResponse {
  owner: SiteMember;
  members: SiteMember[];
  invitations: SiteInvitation[];
}

export interface PublicInvitation {
  site_name: string;
  email: string;
  expires_at: string;
  status: "pending" | "accepted" | "revoked" | "expired";
}

export const fetchSiteMembers = async (siteId: number): Promise<SiteMembersResponse> => {
  const response = await apiClient.get<SiteMembersResponse>(`/dashboard/sites/${siteId}/members`);
  return response.data;
};

export const inviteSiteMember = async (siteId: number, email: string): Promise<SiteInvitation> => {
  const response = await apiClient.post<SiteInvitation>(`/dashboard/sites/${siteId}/invitations`, { email });
  return response.data;
};

export const resendSiteInvitation = async (siteId: number, invitationId: number): Promise<SiteInvitation> => {
  const response = await apiClient.post<SiteInvitation>(
    `/dashboard/sites/${siteId}/invitations/${invitationId}/resend`,
  );
  return response.data;
};

export const revokeSiteInvitation = async (siteId: number, invitationId: number): Promise<void> => {
  await apiClient.delete(`/dashboard/sites/${siteId}/invitations/${invitationId}`);
};

export const updateSiteMember = async (
  siteId: number,
  membershipId: number,
  status: "active" | "suspended",
): Promise<SiteMember> => {
  const response = await apiClient.patch<SiteMember>(
    `/dashboard/sites/${siteId}/members/${membershipId}`,
    { status },
  );
  return response.data;
};

export const removeSiteMember = async (siteId: number, membershipId: number): Promise<void> => {
  await apiClient.delete(`/dashboard/sites/${siteId}/members/${membershipId}`);
};

export const fetchInvitation = async (token: string): Promise<PublicInvitation> => {
  const response = await apiClient.get<PublicInvitation>(`/dashboard/invitations/${token}`);
  return response.data;
};

export const acceptInvitation = async (token: string): Promise<{ site_id: number; role: "member" }> => {
  const response = await apiClient.post(`/dashboard/invitations/${token}/accept`);
  return response.data;
};

export const registerFromInvitation = async (
  token: string,
  data: { password: string; password_confirmation: string },
): Promise<{ token: string; site_id: number; role: "member" }> => {
  const response = await apiClient.post(`/dashboard/invitations/${token}/register`, data);
  return response.data;
};

export const updateOwnerEmail = async (email: string, currentPassword: string): Promise<{ message: string; pending_email: string }> => {
  const response = await apiClient.post("/dashboard/me/update_email", { email, current_password: currentPassword });
  return response.data;
};

export const confirmOwnerEmailChange = async (token: string): Promise<{ message: string }> => {
  const response = await apiClient.post(`/dashboard/me/confirm_email_change/${encodeURIComponent(token)}`);
  return response.data;
};

type Event = {
  id: number;
  name: string;
  description: string;
  ogp_title?: string | null;
  ogp_description?: string | null;
  starts_at: string;
  ends_at: string;
  category_id: number | null;
  category?: {
    id: number;
    name: string;
    slug: string;
  };
  status: "draft" | "ready" | "published" | "active" | "finished" | "cancelled" | "archived";
  required_stock: number;
  stock_locked: boolean;
  actually_started_at?: string;
  actually_ended_at?: string;
  total_registered_stock: number;
  can_start: boolean;
  can_modify_stock: boolean;
  can_register_stock: boolean;
  can_launch: boolean;
  can_revert_to_draft: boolean;
  can_cancel: boolean;
  is_completed: boolean;
  can_archive: boolean;
  can_restore: boolean;
  status_label: string;
  file_urls: string[];
  lottery_animation_url?: string;
  hidden_from_list?: boolean;
  paid_amount_requirement_scope?: 'cumulative' | 'monthly' | null;
  paid_amount_requirement_minimum?: number | null;
  customer_list_ids?: number[];
  customer_list_condition_text?: string | null;
  shipping_request_deadline_days?: number | null;
  point_exchange_deadline_days?: number | null;
  created_at?: string;
};

export interface PaginationMeta {
  current_page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
}

export interface PaginatedEventsResponse {
  events: Event[];
  pagination: PaginationMeta;
}

export const fetchEvents = async (
  siteId: number,
  page: number = 1,
  perPage: number = 10,
  status?: string,
  eventType?: 'oripa' | 'kuji'
): Promise<PaginatedEventsResponse> => {
  const params: any = { page, per_page: perPage };
  if (status) {
    params.status = status;
  }
  if (eventType) {
    params.event_type = eventType;
  }

  const response = await apiClient.get<PaginatedEventsResponse>(`/dashboard/sites/${siteId}/events`, {
    params
  });
  return response.data;
};

export const fetchEvent = async (siteId: number, eventId: number): Promise<Event> => {
  const response = await apiClient.get<Event>(`/dashboard/sites/${siteId}/events/${eventId}`);
  return response.data;
};

export const updateEvent = async (
  siteId: number,
  eventId: number,
  updatedEvent: Partial<Event> | FormData
): Promise<Event> => {
  const url = `/dashboard/sites/${siteId}/events/${eventId}`;

  if (updatedEvent instanceof FormData) {
    const response = await apiClient.put<Event>(url, updatedEvent, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } else {
    const response = await apiClient.put<Event>(url, updatedEvent);
    return response.data;
  }
};

export const createEvent = async (
  siteId: number,
  newEvent: Partial<Event>
): Promise<Event> => {
  const response = await apiClient.post<Event>(
    `/dashboard/sites/${siteId}/events`,
    newEvent
  );
  return response.data;
};

export const deleteEvent = async (siteId: number, eventId: number): Promise<void> => {
  await apiClient.delete(`/dashboard/sites/${siteId}/events/${eventId}`);
};

// Event control functions
export const startEvent = async (siteId: number, eventId: number): Promise<{ message: string; event: Partial<Event> }> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/events/${eventId}/start_event`);
  return response.data;
};

export const finishEvent = async (siteId: number, eventId: number): Promise<{ message: string; event: Partial<Event> }> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/events/${eventId}/finish_event`);
  return response.data;
};

export const cancelEvent = async (siteId: number, eventId: number): Promise<{ message: string; event: Partial<Event> }> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/events/${eventId}/cancel_event`);
  return response.data;
};

export const revertEventToDraft = async (siteId: number, eventId: number): Promise<{ message: string; event: Partial<Event> }> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/events/${eventId}/revert_to_draft`);
  return response.data;
};

// 統合ステータス遷移API
export const transitionEventStatus = async (
  siteId: number,
  eventId: number,
  action: 'mark_as_ready' | 'start' | 'finish' | 'cancel' | 'revert_to_draft'
): Promise<{ message: string; event: Partial<Event> }> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/events/${eventId}/transition_status`, { action_name: action });
  return response.data;
};

export const markEventAsReady = async (siteId: number, eventId: number): Promise<{ message: string; event: Partial<Event> }> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/events/${eventId}/mark_as_ready`);
  return response.data;
};

export const archiveEvent = async (siteId: number, eventId: number): Promise<{ message: string; event: Partial<Event> }> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/events/${eventId}/archive_event`);
  return response.data;
};

export const restoreEvent = async (siteId: number, eventId: number): Promise<{ message: string; event: Partial<Event> }> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/events/${eventId}/restore_event`);
  return response.data;
};

export type Category = {
  id: number;
  name: string;
  slug: string;
  active: boolean;
  position: number;
  protected: boolean;
};

export const fetchCategories = async (siteId: number): Promise<Category[]> => {
  const response = await apiClient.get<Category[]>(`/dashboard/sites/${siteId}/categories`);
  return response.data;
};

export const createCategory = async (siteId: number, category: { name: string; slug: string }): Promise<Category> => {
  const response = await apiClient.post<Category>(`/dashboard/sites/${siteId}/categories`, { category });
  return response.data;
};

export const updateCategory = async (siteId: number, categoryId: number, data: { active?: boolean; name?: string; slug?: string }): Promise<Category> => {
  const response = await apiClient.put<Category>(`/dashboard/sites/${siteId}/categories/${categoryId}`, { category: data });
  return response.data;
};

export const deleteCategory = async (siteId: number, categoryId: number): Promise<void> => {
  await apiClient.delete(`/dashboard/sites/${siteId}/categories/${categoryId}`);
};

type Tag = {
  id: number;
  name: string;
  slug: string;
};

export const fetchTags = async (siteId: number): Promise<Tag[]> => {
  const response = await apiClient.get<Tag[]>(`/dashboard/sites/${siteId}/tags`);
  return response.data;
};

export const createTag = async (siteId: number, tag: { name: string, slug: string }): Promise<Tag> => {
  const response = await apiClient.post<Tag>(`/dashboard/sites/${siteId}/tags`, { tag });
  return response.data;
};

export const deleteTag = async (siteId: number, tagId: number): Promise<void> => {
  await apiClient.delete(`/dashboard/sites/${siteId}/tags/${tagId}`);
};

export const updateCategoryPositions = async (siteId: number, categories: { id: number; position: number }[]): Promise<void> => {
  await apiClient.put(`/dashboard/sites/${siteId}/categories/update_positions`, { categories });
};

type ProductProfile = {
  name: string;
  description: string;
};

type PrizeAllocation = {
  status: string;
};

export type PrizeProduct = {
  id: number;
  product_id: number;
  total_quantity: number;
  sold_quantity: number;
  remaining_quantity: number;
  point_value?: number;
  product: {
    id: number;
    name: string;
    image_url?: string;
    point_value?: number;
  };
};

export type Prize = {
  id: number;
  name: string;
  description: string;
  logic_type?: 'default' | 'just_number' | 'last_one';
  is_active?: boolean;
  winning_number?: number;
  total_quantity?: number;
  sold_quantity?: number;
  remaining_quantity?: number;
  lottery_animation_template_id?: number | null;
  selected_animation_id?: number | null;
  animation_url?: string | null;
  display_thumbnail_mode?: 'products' | 'custom_thumbnails';
  display_thumbnail_urls?: string[];
  display_thumbnail_variant_urls?: Record<string, string>[] | null;
  prize_products?: PrizeProduct[];
};

export type Stock = {
  id: number;
  product_id: number;
  prize_id: number;
  allocated_stock: number;
};

export type ProductType = 'oripa' | 'kuji' | 'common';

export type ContentType = 'physical' | 'digital';

export type Product = {
  id: number;
  name: string;
  description?: string | null;
  image_urls: string[];
  profile: ProductProfile;
  stocks: PrizeAllocation[];
  product_type: ProductType;
  content_type: ContentType;
  digital_file_names?: string[];
  category?: {
    id: number;
    name: string;
    slug: string;
  } | null;
  card_id?: number | null;
  total_stock?: number;
  point_value?: number;
  kuji_events?: {
    id: number;
    name: string;
  }[];
  archived_at?: string | null;
};

export interface PaginatedProductsResponse {
  products: Product[];
  product_quota: {
    active_count: number;
    total_count: number;
    warning_active_limit: number;
    active_limit: number;
    total_limit: number;
    warning: boolean;
  };
  pagination: PaginationMeta;
}

export type StockStatus = 'all' | 'in_stock' | 'out_of_stock';

export type ArchivedFilter = 'active' | 'archived' | 'all';

export const fetchProducts = async (
  siteId: number | string,
  page: number = 1,
  perPage: number = 10,
  categoryId?: number | null,
  stockStatus?: StockStatus,
  productType?: ProductType,
  kujiEventId?: number | string | null,
  eventId?: number | string | null,
  archived?: ArchivedFilter
): Promise<PaginatedProductsResponse> => {
  const params: any = { page, per_page: perPage };
  if (categoryId) {
    params.category_id = categoryId;
  }
  if (stockStatus && stockStatus !== 'all') {
    params.stock_status = stockStatus;
  }
  if (productType) {
    params.product_type = productType;
  }
  // eventId を優先、なければ kujiEventId を使用（後方互換）
  if (eventId) {
    params.event_id = eventId;
  } else if (kujiEventId) {
    params.kuji_event_id = kujiEventId;
  }
  // アーカイブフィルター
  if (archived === 'archived') {
    params.archived = 'true';
  } else if (archived === 'all') {
    params.archived = 'all';
  }
  const response = await apiClient.get<PaginatedProductsResponse>(`/dashboard/sites/${siteId}/products`, {
    params
  });
  return response.data;
};

// プロダクトの詳細を取得
export const fetchProduct = async (siteId: number, productId: number): Promise<Product> => {
  const response = await apiClient.get<Product>(`/dashboard/sites/${siteId}/products/${productId}`, {
    params: { _t: Date.now() }, // キャッシュ回避
  });
  return response.data;
};

// プロダクトを作成
export const createProduct = async (siteId: number, formData: FormData): Promise<Product> => {
  const response = await apiClient.post<Product>(`/dashboard/sites/${siteId}/products`, formData, {
    headers: {
      "Content-Type": "multipart/form-data", // ヘッダーを指定
    },
  });

  return response.data;
};

// プロダクトを更新
export const updateProduct = async (
  siteId: number,
  productId: number,
  formData: FormData
): Promise<Product> => {
  const response = await apiClient.put<Product>(
    `/dashboard/sites/${siteId}/products/${productId}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data", // ヘッダーを指定
      },
    }
  );
  return response.data;
};

// プロダクトをアーカイブ（論理削除）
export const archiveProduct = async (siteId: number, productId: number): Promise<Product> => {
  const response = await apiClient.delete<Product>(`/dashboard/sites/${siteId}/products/${productId}/archive`);
  return response.data;
};

// プロダクトを復元（アーカイブから戻す）
export const restoreProduct = async (siteId: number, productId: number): Promise<Product> => {
  const response = await apiClient.post<Product>(`/dashboard/sites/${siteId}/products/${productId}/restore`);
  return response.data;
};

// アップロードされたファイルの情報
type AttachmentResponse = {
  message: string;
  file_urls: string[];
};

// ファイルをアップロード
export const uploadAttachments = async (
  attachableType: string,
  attachableId: number,
  file: File,
  identifier?: string
): Promise<AttachmentResponse> => {
  const formData = new FormData();
  formData.append("attachable_type", attachableType);
  formData.append("attachable_id", String(attachableId));
  formData.append("file", file);
  formData.append("identifier", identifier);

  const response = await apiClient.post<AttachmentResponse>(
    "/dashboard/attachments",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );

  return response.data;
};

// 特定のファイルを削除
export const deleteAttachment = async (
  fileId: string
): Promise<{ message: string }> => {
  const response = await apiClient.delete<{ message: string }>(`/dashboard/attachments/${fileId}`);

  return response.data;
};

export type Site = {
  id: number;
  name: string;
  description: string;
  subdomain: string;
  privacy_policy?: string;
  terms_of_service?: string;
  privacy_url?: string;
  terms_url?: string;
  trade_law_url?: string;
  faq_url?: string;
  events_count: number;
  oripa_count?: number;
  kuji_count?: number;
  site_type?: 'oripa' | 'kuji';
  products_count: number;
  orders_count: number;
  customers_count: number;
  thumbnail_url?: string;
  icon_url?: string;
  payment_provider?: 'fincode' | 'gmo_pg';
  test_mode?: boolean;
};

export const fetchSite = async (siteId: number): Promise<Site> => {
  const response = await apiClient.get<Site>(`/dashboard/sites/${siteId}`);
  return response.data;
};

export const updateSite = async (
  siteId: number,
  siteData: FormData
): Promise<Site> => {
  const response = await apiClient.put<Site>(`/dashboard/sites/${siteId}`, siteData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};



export const fetchPrizes = async (siteId: number, eventId: number): Promise<Prize[]> => {
  const response = await apiClient.get<Prize[]>(`/dashboard/sites/${siteId}/events/${eventId}/prizes`);
  return response.data;
};

export const createPrize = async (
  siteId: number,
  eventId: number,
  data: { name: string; description?: string; logic_type?: string; is_active?: boolean; winning_number?: number; lottery_animation_template_id?: number | null; selected_animation_id?: number | null } | FormData
): Promise<Prize> => {
  const response = data instanceof FormData
    ? await apiClient.post<Prize>(`/dashboard/sites/${siteId}/events/${eventId}/prizes`, data)
    : await apiClient.post<Prize>(`/dashboard/sites/${siteId}/events/${eventId}/prizes`, { prize: data });
  return response.data;
};

export const updatePrize = async (
  siteId: number,
  eventId: number,
  prizeId: number,
  data: Partial<{ name: string; description?: string; logic_type?: string; is_active?: boolean; winning_number?: number; lottery_animation_template_id?: number | null; selected_animation_id?: number | null; display_thumbnail_mode?: 'products' | 'custom_thumbnails'; remove_display_thumbnails?: boolean }> | FormData
): Promise<Prize> => {
  const response = data instanceof FormData
    ? await apiClient.put<Prize>(`/dashboard/sites/${siteId}/events/${eventId}/prizes/${prizeId}`, data)
    : await apiClient.put<Prize>(`/dashboard/sites/${siteId}/events/${eventId}/prizes/${prizeId}`, { prize: data });
  return response.data;
};

export const deletePrize = async (siteId: number, eventId: number, prizeId: number): Promise<void> => {
  await apiClient.delete(`/dashboard/sites/${siteId}/events/${eventId}/prizes/${prizeId}`);
};

// 賞品に商品を追加
export const addProductToPrize = async (
  siteId: number,
  eventId: number,
  prizeId: number,
  data: { product_id: number; total_quantity: number; point_value?: number }
): Promise<PrizeProduct> => {
  const response = await apiClient.post<PrizeProduct>(
    `/dashboard/sites/${siteId}/events/${eventId}/prizes/${prizeId}/products`,
    { prize_product: data }
  );
  return response.data;
};

// 賞品の商品口数を更新
export const updatePrizeProduct = async (
  siteId: number,
  eventId: number,
  prizeId: number,
  prizeProductId: number,
  data: { total_quantity?: number; point_value?: number }
): Promise<PrizeProduct> => {
  const response = await apiClient.put<PrizeProduct>(
    `/dashboard/sites/${siteId}/events/${eventId}/prizes/${prizeId}/products/${prizeProductId}`,
    { prize_product: data }
  );
  return response.data;
};

// 賞品から商品を削除
export const removePrizeProduct = async (
  siteId: number,
  eventId: number,
  prizeId: number,
  prizeProductId: number
): Promise<void> => {
  await apiClient.delete(
    `/dashboard/sites/${siteId}/events/${eventId}/prizes/${prizeId}/products/${prizeProductId}`
  );
};

export const createEventStock = async (
  siteId: string,
  eventId: number,
  data: { product_id: number; prize_id: number; allocated_stock: number }
): Promise<Stock> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/stocks`, {
    stock: {
      ...data,
      event_id: eventId
    }
  });
  return response.data;
};

export const updateEventStock = async (
  siteId: string,
  eventId: number,
  stockId: number,
  data: { allocated_stock: number }
): Promise<Stock> => {
  const response = await apiClient.patch(`/dashboard/sites/${siteId}/stocks/${stockId}`, {
    stock: data
  });
  return response.data;
};

export const deleteEventStock = async (
  siteId: string,
  eventId: number,
  stockId: number
): Promise<void> => {
  await apiClient.delete(`/dashboard/sites/${siteId}/stocks/${stockId}`);
};

export const fetchEventStocks = async (siteId: string, eventId: number): Promise<Stock[]> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/events/${eventId}/stocks`);
  return response.data;
};

export type LotteryResult = {
  id: number;
  drawn_at: string;
  customer?: {
    id: number;
    email: string;
    home_addresses?: Array<{
      id: number;
      name: string;
      postal_code: string;
      address_line1: string;
      address_line2?: string;
      phone_number: string;
      default: boolean;
    }>;
  };
  // くじ用
  product?: {
    id: number;
    name: string;
    description?: string;
  };
  kuji_grade?: {
    id: number;
    name: string;
  };
  // オリパ用
  lottery_pool?: {
    id: number;
    prize_allocation?: {
      id: number;
      product?: {
        id: number;
        name: string;
        description?: string;
      };
      prize?: {
        id: number;
        name: string;
        description?: string;
      };
    };
  };
};

export type OrderShipping = {
  name: string | null;
  postal_code: string | null;
  address_line1: string | null;
  address_line2: string | null;
  phone_number: string | null;
};

export type Order = {
  id: number;
  uid?: string;
  status: string;
  tracking_number: string;
  notes: string;
  shipping_fee?: number;
  shipping_request_revertible?: boolean;
  product_amount?: number;
  created_at: string;
  updated_at: string;
  shipped_at?: string;
  received_at?: string;
  // 注文時点の配送先スナップショット（顧客が後から住所変更しても変わらない）
  shipping?: OrderShipping | null;
  lottery_results?: LotteryResult[];
};

export type OrdersPagination = {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
};

// 配送画面のタブごとの注文件数（バッジ表示用）
export type OrdersStatusCounts = {
  all: number;
  preparing: number;
  shipping: number;
  delivered: number;
  completed: number;
  others: number;
};

export type OrdersResponse = {
  orders: Order[];
  pagination: OrdersPagination;
  status_counts: OrdersStatusCounts;
};

export type FetchOrdersParams = {
  tab?: string;
  page?: number;
  // 'all' を指定すると CSV エクスポート用に上限付き全件取得
  per_page?: number | 'all';
};

export const fetchOrders = async (
  siteId: string,
  params?: FetchOrdersParams
): Promise<OrdersResponse> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/orders`, { params });
  return response.data;
};

export const fetchOrder = async (siteId: string, orderId: number): Promise<Order> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/orders/${orderId}`);
  return response.data;
};

export const updateOrder = async (siteId: string, orderId: number, data: { status: string, tracking_number?: string, notes?: string }): Promise<Order> => {
  const response = await apiClient.patch(`/dashboard/sites/${siteId}/orders/${orderId}`, { order: data });
  return response.data;
};

export type RevertShippingRequestResponse = {
  id: number;
  status: string;
  reverted_items_count: number;
  message: string;
};

export const revertShippingRequest = async (siteId: string, orderId: number): Promise<RevertShippingRequestResponse> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/orders/${orderId}/revert_shipping_request`);
  return response.data;
};

export type InvalidateShippingRequestResponse = {
  id: number;
  status: string;
  invalidated_items_count: number;
  message: string;
};

export const invalidateShippingRequest = async (siteId: string, orderId: number): Promise<InvalidateShippingRequestResponse> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/orders/${orderId}/invalidate_shipping_request`);
  return response.data;
};

export type ToggleLotteryResultInvalidationResponse = {
  id: number;
  invalidated: boolean;
  status: string;
  message: string;
};

export const toggleLotteryResultInvalidation = async (
  siteId: string,
  lotteryResultId: number
): Promise<ToggleLotteryResultInvalidationResponse> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/lottery_results/${lotteryResultId}/toggle_invalidation`);
  return response.data;
};

export const downloadPackingSlip = async (siteId: string, orderId: number): Promise<void> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/orders/${orderId}/packing_slip`, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  const contentDisposition = response.headers['content-disposition'];
  const filenameMatch = contentDisposition?.match(/filename="?(.+?)"?$/);
  link.download = filenameMatch ? filenameMatch[1] : `packing_slip_${orderId}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const downloadBulkPackingSlips = async (siteId: string, orderIds: number[]): Promise<void> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/orders/bulk_packing_slips`, {
    params: { order_ids: orderIds.join(',') },
    responseType: 'blob',
  });
  const contentDisposition = response.headers['content-disposition'];
  const filenameMatch = contentDisposition?.match(/filename="?(.+?)"?$/);
  const url = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filenameMatch ? filenameMatch[1] : `packing_slips.zip`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Stripe Connect
export type StripeAccountStatus = {
  connected: boolean;
  needs_onboarding: boolean;
  stripe_account?: {
    id: number;
    stripe_account_id: string;
    capabilities_enabled: boolean;
    charges_enabled: boolean;
    payouts_enabled: boolean;
    details_submitted: boolean;
    onboarding_completed_at?: string;
    fully_onboarded?: boolean;
    status_description: string;
  };
};

export const getStripeAccountStatus = async (siteId: number): Promise<StripeAccountStatus> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/stripe_connect/status`);
  return response.data;
};

export const createStripeAccountLink = async (siteId: number): Promise<{ url: string }> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/stripe_connect/create_account_link`);
  return response.data;
};

export const getStripeDashboardLink = async (siteId: number): Promise<{ url: string }> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/stripe_connect/dashboard_link`);
  return response.data;
};

export type DashboardSalesDailyPoint = {
  date: string;
  amount: number;
  count: number;
};

export type DashboardSalesMonthlyPoint = {
  month: string;
  label: string;
  amount: number;
  count: number;
};

export type DashboardSalesOverview = {
  all_time_amount: number;
  today_amount: number;
  current_month_count: number;
  last_7_days_amount: number;
  last_30_days_amount: number;
  current_month_amount: number;
  previous_month_amount: number;
  month_over_month_rate: number | null;
  monthly: DashboardSalesMonthlyPoint[];
  daily: DashboardSalesDailyPoint[];
};

export type DashboardScheduledEvent = {
  id: number;
  name: string;
  status: string;
  event_type: "oripa" | "kuji";
  starts_at: string | null;
  ends_at: string | null;
};

export type DashboardCustomerMonthlyPoint = {
  month: string;
  label: string;
  count: number;
};

export type DashboardData = {
  sales_overview: DashboardSalesOverview;
  customer_overview: {
    monthly: DashboardCustomerMonthlyPoint[];
  };
  event_schedule: {
    upcoming: DashboardScheduledEvent[];
    ending: DashboardScheduledEvent[];
  };
};

export const fetchDashboardData = async (
  siteId: number,
  range?: { startDate: string; endDate: string },
): Promise<DashboardData> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/dashboard`, {
    params: range
      ? { start_date: range.startDate, end_date: range.endDate }
      : undefined,
  });
  return response.data;
};

// Card Search (TCGdex)
export type CardSearchResult = {
  id: string;
  local_id: string;
  name: string;
  image_url: string | null;
  image_url_high: string | null;
};

export type CardDetail = {
  id: string;
  local_id: string;
  name: string;
  category: string;
  image_url: string | null;
  image_url_high: string | null;
  illustrator: string | null;
  rarity: string | null;
  hp: number | null;
  types: string[] | null;
  description: string | null;
  set: {
    id: string;
    name: string;
    card_count: { official: number; total: number } | null;
  } | null;
};

export const searchCards = async (
  query: string,
  gameType: string = "pokemon",
  limit: number = 20
): Promise<CardSearchResult[]> => {
  const response = await apiClient.get("/dashboard/card_search", {
    params: { query, game_type: gameType, limit },
  });
  return response.data.cards;
};

export const getCardDetail = async (
  cardId: string,
  gameType: string = "pokemon"
): Promise<CardDetail> => {
  const response = await apiClient.get(`/dashboard/card_search/${cardId}`, {
    params: { game_type: gameType },
  });
  return response.data.card;
};

// カードを選択してCardsマスターテーブルに保存
export type SelectedCard = {
  id: number;  // DBのcard_id
  source: string;
  source_card_id: string;
  game_type: string;
  name: string;
  rarity: string | null;
  image_url: string | null;
  image_url_high: string | null;
  set_code: string | null;
  set_name: string | null;
  card_number: string | null;
};

export const selectCard = async (
  sourceCardId: string,
  gameType: string = "pokemon"
): Promise<SelectedCard> => {
  const response = await apiClient.post("/dashboard/card_search/select", {
    source_card_id: sourceCardId,
    game_type: gameType,
  });
  return response.data.card;
};

// ProductStock API
export type ProductStock = {
  id?: number;
  product_id?: number;
  grade?: string;
  description?: string;
  point_value?: number;
  status: "available" | "reserved" | "sold";
  image_urls?: string[];
  allocated?: boolean;
  event?: { id: number; name: string } | null;
  prize_allocation_id?: number | null;
};

export const fetchProductStocks = async (
  siteId: number,
  productId: number
): Promise<{ product_stocks: ProductStock[] }> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/products/${productId}/product_stocks`);
  return response.data;
};

export const createProductStock = async (
  siteId: number,
  productId: number,
  formData: FormData
): Promise<ProductStock> => {
  const response = await apiClient.post(
    `/dashboard/sites/${siteId}/products/${productId}/product_stocks`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return response.data;
};

export const bulkCreateProductStock = async (
  siteId: number,
  productId: number,
  params: {
    count: number;
    grade?: string;
    description?: string;
    point_value?: number;
  }
): Promise<{ message: string; product_stocks: ProductStock[] }> => {
  const response = await apiClient.post(
    `/dashboard/sites/${siteId}/products/${productId}/product_stocks/bulk_create`,
    {
      count: params.count,
      product_stock: {
        grade: params.grade || "none",
        description: params.description,
        point_value: params.point_value,
        status: "available",
      },
    }
  );
  return response.data;
};

export const updateProductStock = async (
  siteId: number,
  productId: number,
  stockId: number,
  formData: FormData
): Promise<ProductStock> => {
  const response = await apiClient.put(
    `/dashboard/sites/${siteId}/products/${productId}/product_stocks/${stockId}`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return response.data;
};

export const deleteProductStock = async (
  siteId: number,
  productId: number,
  stockId: number
): Promise<void> => {
  await apiClient.delete(`/dashboard/sites/${siteId}/products/${productId}/product_stocks/${stockId}`);
};

// サイト直下の在庫一覧（全ProductStock）
export type SiteProductStock = {
  id: number;
  product: {
    id: number;
    name: string;
    image_url: string | null;
  };
  grade: string;
  description: string | null;
  point_value: number | null;
  status: "available" | "reserved" | "sold";
  allocated: boolean;
  event: { id: number; name: string; status: string } | null;
  created_at: string;
  updated_at: string;
};

export type SiteProductStocksResponse = {
  product_stocks: SiteProductStock[];
  pagination: PaginationMeta;
};

export const fetchSiteProductStocks = async (
  siteId: number,
  params?: {
    page?: number;
    per_page?: number;
    product_id?: number;
    grade?: string;
    status?: string;
  }
): Promise<SiteProductStocksResponse> => {
  const response = await apiClient.get<SiteProductStocksResponse>(
    `/dashboard/sites/${siteId}/product_stocks`,
    { params }
  );
  return response.data;
};

// イベント作成（在庫選択から）
export type CreateEventWithStocksParams = {
  event: {
    name: string;
    description?: string;
    starts_at?: string;
    ends_at?: string;
    category_id?: number;
    shipping_request_deadline_days?: number;
    point_exchange_deadline_days?: number;
  };
  product_stock_ids: number[];
};

export const createEventWithStocks = async (
  siteId: number,
  params: CreateEventWithStocksParams
): Promise<any> => {
  const response = await apiClient.post(
    `/dashboard/sites/${siteId}/events/create_with_stocks`,
    params
  );
  return response.data;
};

// 既存イベントへ在庫補填
export const addStocksToEvent = async (
  siteId: number,
  eventId: number,
  productStockIds: number[]
): Promise<any> => {
  const response = await apiClient.post(
    `/dashboard/sites/${siteId}/events/${eventId}/allocations/add_stocks`,
    { product_stock_ids: productStockIds }
  );
  return response.data;
};

// PrizeAllocation型（イベント内の在庫割当）
export type EventPrizeAllocation = {
  id: number;
  product_stock: {
    id: number;
    grade: string;
    description: string | null;
    point_value: number | null;
    product: {
      id: number;
      name: string;
      image_url: string | null;
    };
  };
  prize: {
    id: number;
    name: string;
  } | null;
  allocated_stock: number;
};

// イベントの在庫割当一覧を取得（新API）
export const fetchEventAllocations = async (
  siteId: number,
  eventId: number
): Promise<EventPrizeAllocation[]> => {
  const response = await apiClient.get(
    `/dashboard/sites/${siteId}/events/${eventId}/allocations`
  );
  return response.data.allocations;
};

// 在庫を賞品に割り当て
export const assignPrizeToAllocation = async (
  siteId: number,
  eventId: number,
  allocationId: number,
  prizeId: number | null
): Promise<EventPrizeAllocation> => {
  const response = await apiClient.put(
    `/dashboard/sites/${siteId}/events/${eventId}/prize_allocations/${allocationId}/assign_prize`,
    { prize_id: prizeId }
  );
  return response.data;
};

// イベントに追加可能な在庫一覧を取得
export type AvailableStock = {
  id: number;
  grade: string;
  description: string | null;
  point_value: number | null;
  status: string;
  product: {
    id: number;
    name: string;
    image_urls: string[];
  };
};

export const fetchAvailableStocks = async (
  siteId: number,
  eventId: number
): Promise<AvailableStock[]> => {
  const response = await apiClient.get(
    `/dashboard/sites/${siteId}/events/${eventId}/allocations/available_stocks`
  );
  return response.data.product_stocks;
};

// 在庫をイベントに追加
export const addStocksToEventAllocations = async (
  siteId: number,
  eventId: number,
  productStockIds: number[]
): Promise<{ message: string; allocations: EventPrizeAllocation[] }> => {
  const response = await apiClient.post(
    `/dashboard/sites/${siteId}/events/${eventId}/allocations/add_stocks`,
    { product_stock_ids: productStockIds }
  );
  return response.data;
};

// イベントから在庫を削除（元に戻す）
export const removeAllocationFromEvent = async (
  siteId: number,
  eventId: number,
  allocationId: number
): Promise<void> => {
  await apiClient.delete(
    `/dashboard/sites/${siteId}/events/${eventId}/prize_allocations/${allocationId}`
  );
};

// 複数の在庫に一括で賞品を割り当て
export const bulkAssignPrize = async (
  siteId: number,
  eventId: number,
  allocationIds: number[],
  prizeId: number | null
): Promise<{ message: string; updated_count: number }> => {
  const response = await apiClient.post(
    `/dashboard/sites/${siteId}/events/${eventId}/prize_allocations/bulk_assign_prize`,
    { allocation_ids: allocationIds, prize_id: prizeId }
  );
  return response.data;
};

// 複数の在庫をイベントから一括削除
export const bulkRemoveAllocations = async (
  siteId: number,
  eventId: number,
  allocationIds: number[]
): Promise<{ message: string; deleted_count: number }> => {
  const response = await apiClient.delete(
    `/dashboard/sites/${siteId}/events/${eventId}/prize_allocations/bulk_destroy`,
    { data: { allocation_ids: allocationIds } }
  );
  return response.data;
};

// 顧客へのポイントチャージ
export type ChargePointsResponse = {
  success: boolean;
  balance: number;
  message: string;
};

export const chargePoints = async (
  siteId: number,
  customerId: number,
  amount: number,
  reason?: string,
  note?: string
): Promise<ChargePointsResponse> => {
  const response = await apiClient.post<ChargePointsResponse>(
    `/dashboard/sites/${siteId}/customers/${customerId}/charge_points`,
    { amount, reason, note }
  );
  return response.data;
};

// 顧客のポイント減額
export const deductPoints = async (
  siteId: number,
  customerId: number,
  amount: number,
  reason?: string,
  note?: string
): Promise<ChargePointsResponse> => {
  const response = await apiClient.post<ChargePointsResponse>(
    `/dashboard/sites/${siteId}/customers/${customerId}/deduct_points`,
    { amount, reason, note }
  );
  return response.data;
};

// 顧客退会
export const deleteCustomerAccount = async (
  siteId: number,
  customerId: number,
  reason?: string
): Promise<{ message: string }> => {
  const response = await apiClient.delete(
    `/dashboard/sites/${siteId}/customers/${customerId}`,
    { data: { reason } }
  );
  return response.data;
};

// 通知設定
export type NotificationSettings = {
  login_notification: boolean;
  shipping_notification: boolean;
  lottery_notification: boolean;
  marketing_notification: boolean;
};

export const fetchNotificationSettings = async (): Promise<NotificationSettings> => {
  const response = await apiClient.get<NotificationSettings>('/dashboard/notification_settings');
  return response.data;
};

export const updateNotificationSettings = async (
  settings: Partial<NotificationSettings>
): Promise<NotificationSettings & { message: string }> => {
  const response = await apiClient.patch('/dashboard/notification_settings', settings);
  return response.data;
};

// 決済手段設定
export type PaymentSettings = {
  credit_card_enabled: boolean;
  bank_transfer_enabled: boolean;
  paidy_enabled: boolean;
  customer_bank_transfer_info: BankTransferInfo;
  customer_bank_transfer_registered: boolean;
  available_payment_methods: ('credit_card' | 'bank_transfer' | 'paidy')[];
  // 3軸制御: カードブランド・QR・ウォレットの有効化手段（Owner が顧客に出す手段）
  accepted_payment_methods: string[];
  // Admin が許可した決済手段集合（Owner はこの範囲内でのみ有効化できる）
  admin_available_payment_methods: string[];
  payment_method_review_statuses: Record<string, PaymentMethodReviewStatus>;
};

export type PaymentMethodReviewStatus = {
  status: 'not_requested' | 'pending' | 'approved' | 'rejected';
  requested_at?: string;
  reviewed_at?: string;
  reviewed_by_admin_user_id?: number;
};

export type BankTransferInfo = {
  bank_name?: string;
  branch_name?: string;
  account_type?: '普通' | '当座';
  account_number?: string;
  account_holder?: string;
};

export const fetchPaymentSettings = async (siteId: number): Promise<PaymentSettings> => {
  const response = await apiClient.get<PaymentSettings>(`/dashboard/sites/${siteId}/payment_settings`);
  return response.data;
};

export const updatePaymentSettings = async (
  siteId: number,
  settings: Partial<{
    credit_card_enabled: boolean;
    bank_transfer_enabled: boolean;
    paidy_enabled: boolean;
  }>
): Promise<PaymentSettings & { message: string }> => {
  const response = await apiClient.patch(`/dashboard/sites/${siteId}/payment_settings`, settings);
  return response.data;
};

export const updateBankTransferInfo = async (
  siteId: number,
  bankInfo: BankTransferInfo
): Promise<{ message: string; customer_bank_transfer_info: BankTransferInfo; customer_bank_transfer_registered: boolean }> => {
  const response = await apiClient.patch(`/dashboard/sites/${siteId}/payment_settings/bank_transfer_info`, bankInfo);
  return response.data;
};

// 決済手段（accepted_payment_methods）取得
export const fetchAcceptedPaymentMethods = async (
  siteId: number
): Promise<{
  accepted_payment_methods: string[];
  admin_available_payment_methods: string[];
  payment_method_review_statuses: Record<string, PaymentMethodReviewStatus>;
}> => {
  const response = await apiClient.get<PaymentSettings>(`/dashboard/sites/${siteId}/payment_settings`);
  return {
    accepted_payment_methods: response.data.accepted_payment_methods ?? [],
    admin_available_payment_methods: response.data.admin_available_payment_methods ?? [],
    payment_method_review_statuses: response.data.payment_method_review_statuses ?? {},
  };
};

// 決済手段（accepted_payment_methods）更新
export const updateAcceptedPaymentMethods = async (
  siteId: number,
  acceptedPaymentMethods: string[]
): Promise<{ accepted_payment_methods: string[] }> => {
  const response = await apiClient.patch(`/dashboard/sites/${siteId}/payment_settings`, {
    accepted_payment_methods: acceptedPaymentMethods,
  });
  return response.data;
};

export const requestPaymentMethodReview = async (
  siteId: number,
  paymentMethod: string
): Promise<{
  message: string;
  payment_method_review_statuses: Record<string, PaymentMethodReviewStatus>;
}> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/payment_settings/request_review`, {
    payment_method: paymentMethod,
  });
  return response.data;
};

// 決済トランザクション
export type PaymentTransaction = {
  id: number;
  uid: string;
  payable_type: 'PointCharge' | 'Checkout' | null;
  payable_type_label: string;
  provider: 'fincode' | 'stripe' | 'payjp' | 'gmo_pg';
  payment_method: string | null;
  payment_method_label: string;
  provider_transaction_id: string;
  amount: number;
  product_amount: number;
  shipping_fee: number;
  status: 'pending' | 'awaiting_payment' | 'completed' | 'failed' | 'cancelled' | 'expired';
  status_label: string;
  test_mode: boolean;
  customer_email: string | null;
  customer_id: number | null;
  event_name: string | null;
  event_id: number | null;
  checkout_group_id: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
};

export type PaymentTransactionDetail = PaymentTransaction & {
  test_mode: boolean;
  customer: {
    id: number;
    email: string;
    uid: string;
  } | null;
  event_name: string | null;
  event_id: number | null;
  event_uid: string | null;
  checkout_details: {
    quantity: number;
    is_bulk_purchase: boolean;
    shipping_fee_per_unit: number | null;
    shipping_fee_tier_quantity: number | null;
    total_shipping_fee: number | null;
    points_used: number;
    card_amount: number;
  } | null;
  lottery_results: {
    id: number;
    event_name: string | null;
    prize_name: string | null;
    product_name: string | null;
    is_bulk_bonus: boolean;
    is_last_one_prize: boolean;
    created_at: string;
  }[];
  point_charge_details: {
    granted_pt: number;
    charge_type: string;
    charge_type_label: string;
  } | null;
  provider_dashboard_url: string | null;
};

export type PaymentTransactionsPagination = {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
};

export type PaymentTransactionsSummary = {
  total_amount: number;
  total_product_amount: number;
  total_shipping_fee: number;
  total_count: number;
  completed_count: number;
  pending_count: number;
  failed_count: number;
  cancelled_count: number;
};

export type FetchPaymentTransactionsParams = {
  page?: number;
  per_page?: number;
  payable_type?: string;
  status?: string;
  provider?: string;
  search?: string;
  start_date?: string;
  end_date?: string;
  customer_id?: string;
  include_summary?: boolean;
};

export const fetchPaymentTransactions = async (
  siteId: number,
  params?: FetchPaymentTransactionsParams
): Promise<{
  transactions: PaymentTransaction[];
  pagination: PaymentTransactionsPagination;
  summary?: PaymentTransactionsSummary;
}> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/payment_transactions`, { params });
  return response.data;
};

export const fetchPaymentTransaction = async (
  siteId: number,
  transactionId: number
): Promise<PaymentTransactionDetail> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/payment_transactions/${transactionId}`);
  return response.data;
};

export const cancelPaymentTransaction = async (
  siteId: number,
  transactionId: number
): Promise<PaymentTransactionDetail> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/payment_transactions/${transactionId}/cancel`);
  return response.data;
};

// 法的文書管理
export type LegalDocumentSummary = {
  document_type: 'terms' | 'privacy' | 'trade_law';
  label: string;
  current_version: number | null;
  latest_version: number | null;
  has_draft: boolean;
  last_published_at: string | null;
  last_updated_at: string | null;
  use_external_url: boolean;
  external_url: string | null;
};

export type LegalDocumentVersion = {
  id: number;
  version: number;
  content: string;
  published: boolean;
  published_at: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
};

export type LegalDocumentVersionSummary = {
  id: number;
  version: number;
  published: boolean;
  published_at: string | null;
  updated_by: string | null;
  created_at: string;
};

export type LegalDocumentDetail = {
  document_type: string;
  label: string;
  current: LegalDocumentVersion | null;
  latest: LegalDocumentVersion | null;
  versions: LegalDocumentVersionSummary[];
};

export const fetchLegalDocuments = async (siteId: number): Promise<{ documents: LegalDocumentSummary[] }> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/legal_documents`);
  return response.data;
};

export const fetchLegalDocument = async (
  siteId: number,
  documentType: string
): Promise<LegalDocumentDetail> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/legal_documents/${documentType}`);
  return response.data;
};

export const saveLegalDocument = async (
  siteId: number,
  documentType: string,
  content: string,
  publish: boolean = true
): Promise<{ message: string; version: LegalDocumentVersion }> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/legal_documents/${documentType}`, {
    content,
    publish
  });
  return response.data;
};

export const revertLegalDocument = async (
  siteId: number,
  documentType: string,
  version: number
): Promise<{ message: string; version: LegalDocumentVersion }> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/legal_documents/${documentType}/revert`, {
    version
  });
  return response.data;
};

export const publishLegalDocument = async (
  siteId: number,
  documentType: string
): Promise<{ message: string; version: LegalDocumentVersion }> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/legal_documents/${documentType}/publish`);
  return response.data;
};

export const updateLegalDocumentExternalUrl = async (
  siteId: number,
  documentType: string,
  useExternalUrl: boolean,
  externalUrl: string
): Promise<{ message: string; use_external_url: boolean; external_url: string | null }> => {
  const response = await apiClient.patch(`/dashboard/sites/${siteId}/legal_documents/${documentType}/external_url`, {
    use_external_url: useExternalUrl,
    external_url: externalUrl
  });
  return response.data;
};

export type LegalDocumentTemplate = {
  document_type: string;
  content: string;
  disclaimer: string;
};

export const fetchLegalDocumentTemplate = async (
  siteId: number,
  documentType: string
): Promise<LegalDocumentTemplate> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/legal_documents/${documentType}/template`);
  return response.data;
};

// Site Banners
export type SiteBanner = {
  id: number;
  image_url: string | null;
  image_variant_urls: Record<string, string> | null;
  link_url: string | null;
  visible: boolean;
  position: number;
  created_at: string;
  updated_at: string;
};

export const fetchSiteBanners = async (siteId: number): Promise<SiteBanner[]> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/site_banners`);
  return response.data;
};

export const createSiteBanner = async (
  siteId: number,
  formData: FormData
): Promise<SiteBanner> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/site_banners`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

export const updateSiteBanner = async (
  siteId: number,
  bannerId: number,
  formData: FormData
): Promise<SiteBanner> => {
  const response = await apiClient.put(`/dashboard/sites/${siteId}/site_banners/${bannerId}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

export const deleteSiteBanner = async (siteId: number, bannerId: number): Promise<void> => {
  await apiClient.delete(`/dashboard/sites/${siteId}/site_banners/${bannerId}`);
};

export const reorderSiteBanners = async (
  siteId: number,
  positions: number[]
): Promise<{ message: string }> => {
  const response = await apiClient.put(`/dashboard/sites/${siteId}/site_banners/reorder`, { positions });
  return response.data;
};

// イベント並び順更新
export const reorderEvents = async (
  siteId: number,
  positions: number[]
): Promise<{ message: string }> => {
  const response = await apiClient.put(`/dashboard/sites/${siteId}/events/reorder`, { positions });
  return response.data;
};

// 友達招待キャンペーン
export type ReferralCampaign = {
  id: number;
  name: string;
  status: 'draft' | 'active' | 'paused' | 'completed';
  inviter_points: number;
  invitee_points: number;
  budget_limit: number | null;
  max_invitations_per_inviter: number | null;
  total_awarded: number;
  budget_remaining: number | null;
  budget_usage_percentage: number;
  referrals_count: number;
  completed_referrals_count: number;
  starts_at: string | null;
  ends_at: string | null;
  conversion_point: 'registration' | 'phone_verified' | 'first_purchase';
  can_award_points: boolean;
  can_activate: boolean;
  created_at: string;
  updated_at: string;
  referrals?: ReferralRecord[];
};

export type ReferralRecord = {
  id: number;
  inviter_id: number;
  inviter_email: string;
  invitee_id: number;
  invitee_email: string;
  status: 'pending' | 'completed' | 'expired';
  inviter_points_awarded: number;
  invitee_points_awarded: number;
  completed_at: string | null;
  created_at: string;
  invitee_status?: {
    email_verified: boolean;
    has_purchased: boolean;
    balance: number;
  };
};

export type CreateReferralCampaignParams = {
  name: string;
  inviter_points: number;
  invitee_points: number;
  budget_limit?: number | null;
  max_invitations_per_inviter?: number | null;
  starts_at?: string | null;
  ends_at?: string | null;
  conversion_point?: 'registration' | 'phone_verified' | 'first_purchase';
};

export const fetchReferralCampaigns = async (
  siteId: number
): Promise<{ campaigns: ReferralCampaign[] }> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/referral_campaigns`);
  return response.data;
};

export type ReferralCampaignDetailParams = {
  page?: number;
  status?: 'pending' | 'completed';
  search?: string;
};

export type ReferralCampaignDetailResponse = {
  campaign: ReferralCampaign;
  referrals: ReferralRecord[];
  meta: PaginationMeta;
};

export const fetchReferralCampaign = async (
  siteId: number,
  campaignId: number,
  params?: ReferralCampaignDetailParams
): Promise<ReferralCampaignDetailResponse> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/referral_campaigns/${campaignId}`, { params });
  return response.data;
};

export const createReferralCampaign = async (
  siteId: number,
  params: CreateReferralCampaignParams
): Promise<{ success: boolean; campaign: ReferralCampaign }> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/referral_campaigns`, {
    referral_campaign: params
  });
  return response.data;
};

export const updateReferralCampaign = async (
  siteId: number,
  campaignId: number,
  params: Partial<CreateReferralCampaignParams>
): Promise<{ success: boolean; campaign: ReferralCampaign }> => {
  const response = await apiClient.put(`/dashboard/sites/${siteId}/referral_campaigns/${campaignId}`, {
    referral_campaign: params
  });
  return response.data;
};

export const deleteReferralCampaign = async (
  siteId: number,
  campaignId: number
): Promise<{ success: boolean }> => {
  const response = await apiClient.delete(`/dashboard/sites/${siteId}/referral_campaigns/${campaignId}`);
  return response.data;
};

export const activateReferralCampaign = async (
  siteId: number,
  campaignId: number
): Promise<{ success: boolean; campaign: ReferralCampaign }> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/referral_campaigns/${campaignId}/activate`);
  return response.data;
};

export const pauseReferralCampaign = async (
  siteId: number,
  campaignId: number
): Promise<{ success: boolean; campaign: ReferralCampaign }> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/referral_campaigns/${campaignId}/pause`);
  return response.data;
};

export const completeReferralCampaign = async (
  siteId: number,
  campaignId: number
): Promise<{ success: boolean; campaign: ReferralCampaign }> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/referral_campaigns/${campaignId}/complete`);
  return response.data;
};

// ========================================
// サイト設定
// ========================================

export type SiteSetting = {
  referral_enabled: boolean;
  coupon_enabled: boolean;
  show_terms: boolean;
  show_privacy: boolean;
  show_trade_law: boolean;
  show_faq: boolean;
  show_contact: boolean;
  show_powered_by: boolean;
  require_phone_verification: boolean;
  require_birth_date_verification: boolean;
  email_header_color: string | null;
  email_button_color: string | null;
  email_show_logo: boolean;
  x_account: string | null;
  line_url: string | null;
  basic_auth_enabled: boolean;
  basic_auth_username: string | null;
  advertising_tags: AdvertisingTags;
};

export type AdvertisingTags = {
  gtm_container_id?: string;
  ga4_measurement_id?: string;
  meta_pixel_id?: string;
};

export type BasicAuthCredentials = {
  message: string;
  basic_auth_enabled: boolean;
  basic_auth_username: string;
  basic_auth_password: string;
};

export const fetchSiteSetting = async (siteId: number): Promise<SiteSetting> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/setting`);
  return response.data;
};

export const updateSiteSetting = async (
  siteId: number,
  params: Partial<SiteSetting>
): Promise<SiteSetting> => {
  const response = await apiClient.patch(`/dashboard/sites/${siteId}/setting`, {
    site_setting: params
  });
  return response.data;
};

// Basic認証を有効化
export const enableBasicAuth = async (
  siteId: number
): Promise<BasicAuthCredentials> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/setting/enable_basic_auth`);
  return response.data;
};

// Basic認証を無効化
export const disableBasicAuth = async (
  siteId: number
): Promise<{ message: string; basic_auth_enabled: boolean }> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/setting/disable_basic_auth`);
  return response.data;
};

// Basic認証パスワードを再生成
export const regenerateBasicAuthPassword = async (
  siteId: number
): Promise<BasicAuthCredentials> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/setting/regenerate_basic_auth_password`);
  return response.data;
};

// Basic認証パスワードを表示
export const revealBasicAuthPassword = async (
  siteId: number
): Promise<{ basic_auth_username: string; basic_auth_password: string }> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/setting/reveal_basic_auth_password`);
  return response.data;
};

// ========================================
// セットアップウィザード
// ========================================

export type SetupProgress = {
  store_info_complete: boolean;
  trade_law_complete: boolean;
  terms_complete: boolean;
  products_complete: boolean;
  events_complete: boolean;
  setup_complete: boolean;
  setup_approved: boolean;
  stripe_connected: boolean;
};

export const fetchSetupProgress = async (siteId: number): Promise<SetupProgress> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/setup_wizard`);
  return response.data;
};

// ========================================
// テーマ設定
// ========================================

export type ThemeConfig = {
  // カラー設定
  nav_color: string;
  primary_color: string;
  secondary_color: string;
  bg_color: string;
  // フォント設定
  title_font: string;
  title_size: 'small' | 'medium' | 'large' | 'xlarge';
  body_font: string;
  // ロゴ設定
  logo_type: 'text' | 'image';
  logo_text: string | null;
  logo_image_url: string | null;
  // デザインスタイル
  design_style: 'neobrutalism' | 'modern';
  // 選択肢（API から取得）
  font_options: string[];
  title_size_options: string[];
  design_style_options: string[];
};

export type ThemeUpdateParams = {
  // カラー設定
  nav_color?: string;
  primary_color?: string;
  secondary_color?: string;
  bg_color?: string;
  // フォント設定
  title_font?: string;
  title_size?: 'small' | 'medium' | 'large' | 'xlarge';
  body_font?: string;
  // ロゴ設定
  logo_type?: 'text' | 'image';
  logo_text?: string | null;
  // デザインスタイル
  design_style?: 'neobrutalism' | 'modern';
};

export const fetchTheme = async (siteId: number): Promise<ThemeConfig> => {
  const response = await apiClient.get<ThemeConfig>(`/dashboard/sites/${siteId}/theme`);
  return response.data;
};

export const updateTheme = async (
  siteId: number,
  themeConfig: ThemeUpdateParams | FormData
): Promise<ThemeConfig> => {
  if (themeConfig instanceof FormData) {
    const response = await apiClient.put<ThemeConfig>(`/dashboard/sites/${siteId}/theme`, themeConfig, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  }
  const response = await apiClient.put<ThemeConfig>(`/dashboard/sites/${siteId}/theme`, themeConfig);
  return response.data;
};

// === 演出動画ライブラリ ===
export type SiteAnimation = {
  id: number;
  filename: string;
  content_type: string;
  byte_size: number;
  url: string;
  created_at: string;
};

export type SiteAnimationUsage = {
  total_files: number;
  total_bytes: number;
  warning: boolean;
  upload_allowed: boolean;
  limits: {
    max_files: number;
    max_bytes: number;
    warning_files: number;
    warning_bytes: number;
  };
};

export const fetchSiteAnimationsWithUsage = async (siteId: number): Promise<{ animations: SiteAnimation[]; usage: SiteAnimationUsage }> => {
  const response = await apiClient.get<{ animations: SiteAnimation[]; usage: SiteAnimationUsage }>(`/dashboard/sites/${siteId}/animations`);
  return response.data;
};

export const fetchSiteAnimations = async (siteId: number): Promise<SiteAnimation[]> => {
  const response = await fetchSiteAnimationsWithUsage(siteId);
  return response.animations;
};

export const uploadSiteAnimation = async (siteId: number, file: File): Promise<SiteAnimation> => {
  const formData = new FormData();
  formData.append('animation', file);
  const response = await apiClient.post<{ animation: SiteAnimation }>(`/dashboard/sites/${siteId}/animations`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data.animation;
};

export const deleteSiteAnimation = async (siteId: number, animationId: number): Promise<void> => {
  await apiClient.delete(`/dashboard/sites/${siteId}/animations/${animationId}`);
};

// ========================================
// くじ機能
// ========================================

export type ProbabilityMode = 'stock_based' | 'fixed_rate';

export type KujiGrade = {
  id: number;
  name: string;
  title: string | null;
  description: string | null;
  probability: number;
  effective_probability: number;
  position: number;
  initial_stock: number | null;
  remaining_stock: number | null;
  unlimited_stock: boolean;
  sold_out: boolean;
  drawable: boolean;
  products_count: number;
  lottery_animation_template_id: number | null;
  selected_animation_id: number | null;
  animation_url: string | null;
  products?: {
    id: number;
    name: string;
    image_url: string | null;
    total_stock: number;
    available_stock_count: number;
  }[];
};

export type LastOneProduct = {
  id: number;
  name: string;
  image_urls: string[];
  total_stock: number;
};

export type BulkBonus = {
  quantity: number;
  label?: string;
  product: LastOneProduct | null;
};

export type KujiConfig = {
  id: number;
  probability_mode: ProbabilityMode;
  total_initial_stock: number;
  total_remaining_stock: number;
  drawable: boolean;
  free_shipping: boolean;
  shipping_fee_tier_quantity: number;
  shipping_fee_per_tier: number;
  min_quantity: number;
  max_quantity: number;
  bulk_quantities: number[];
  kuji_grades: KujiGrade[];
  last_one_enabled: boolean;
  last_one_product: LastOneProduct | null;
  bulk_bonus_enabled: boolean;
  bulk_bonuses: BulkBonus[];
  pt_exchange_enabled: boolean;
  pt_exchange_value: number | null;
  auto_ship_deadline: string | null;
};

export type KujiGradesResponse = {
  kuji_grades: KujiGrade[];
  probability_sum: number;
  probability_mode: ProbabilityMode;
};

// くじ設定取得
export const fetchKujiConfig = async (
  siteId: number,
  eventId: number
): Promise<KujiConfig> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/events/${eventId}/kuji_config`);
  return response.data;
};

// くじ設定作成
export const createKujiConfig = async (
  siteId: number,
  eventId: number,
  params: { probability_mode: ProbabilityMode }
): Promise<KujiConfig> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/events/${eventId}/kuji_config`, {
    kuji_config: params
  });
  return response.data;
};

// くじ設定更新
export const updateKujiConfig = async (
  siteId: number,
  eventId: number,
  params: {
    probability_mode?: ProbabilityMode;
    shipping_fee_tier_quantity?: number;
    shipping_fee_per_tier?: number;
    min_quantity?: number;
    max_quantity?: number;
    bulk_quantities?: number[];
    last_one_enabled?: boolean;
    last_one_product_id?: number | null;
    bulk_bonus_enabled?: boolean;
    pt_exchange_enabled?: boolean;
    pt_exchange_value?: number | null;
    auto_ship_deadline?: string | null;
  }
): Promise<KujiConfig> => {
  const response = await apiClient.patch(`/dashboard/sites/${siteId}/events/${eventId}/kuji_config`, {
    kuji_config: params
  });
  return response.data;
};

// セット購入特典 設定
export const setBulkBonusProduct = async (
  siteId: number,
  eventId: number,
  quantity: number,
  productId: number,
  label?: string
): Promise<KujiConfig> => {
  const response = await apiClient.put(`/dashboard/sites/${siteId}/events/${eventId}/kuji_config/bulk_bonus`, {
    quantity,
    product_id: productId,
    label
  });
  return response.data;
};

// セット購入特典 解除
export const removeBulkBonusProduct = async (
  siteId: number,
  eventId: number,
  quantity: number
): Promise<KujiConfig> => {
  const response = await apiClient.delete(`/dashboard/sites/${siteId}/events/${eventId}/kuji_config/bulk_bonus`, {
    data: { quantity }
  });
  return response.data;
};

// くじ一括配送プレビュー
export type AutoShipPreview = {
  total_pending: number;
  shippable_count: number;
  no_address_count: number;
  customer_count: number;
};

export const fetchAutoShipPreview = async (
  siteId: number,
  eventId: number
): Promise<AutoShipPreview> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/events/${eventId}/auto_ship_preview`);
  return response.data;
};

// くじ一括配送
export const autoShipKujiPrizes = async (
  siteId: number,
  eventId: number
): Promise<{ message: string; shipped_count: number; skipped_count: number; errors: string[] }> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/events/${eventId}/auto_ship`);
  return response.data;
};

// イベント当選結果一覧
export type EventLotteryResult = {
  id: number;
  customer_id: number;
  customer_email: string | null;
  grade_name: string | null;
  product_name: string | null;
  status: 'pending' | 'ordered' | 'point_converted';
  downloaded: boolean;
  can_ship: boolean;
  can_convert: boolean;
  shipping_request_deadline_at: string | null;
  point_exchange_deadline_at: string | null;
  shipping_request_deadline_expired: boolean;
  point_exchange_deadline_expired: boolean;
  point_value: number | null;
  checkout_group_id: string | null;
  is_bulk_bonus: boolean;
  is_last_one_prize: boolean;
  created_at: string;
};

export type EventLotteryResultsSummary = {
  total: number;
  pending_count: number;
  ordered_count: number;
  point_converted_count: number;
  downloaded_count: number;
  grade_stats: {
    total: number;
    grades: Array<{
      name: string;
      count: number;
      percentage: number;
    }>;
  };
};

export type EventLotteryResultsResponse = {
  lottery_results: EventLotteryResult[];
  summary: EventLotteryResultsSummary;
  pagination: {
    page: number;
    current_page: number;
    per_page: number;
    total: number;
    total_count: number;
    total_pages: number;
    export_limit?: number;
    truncated?: boolean;
  };
};

export type EventLotteryResultsExportResponse = Omit<EventLotteryResultsResponse, 'summary'>;

export const fetchEventLotteryResults = async (
  siteId: number,
  eventId: number,
  status?: string,
  page = 1,
  perPage = 100
): Promise<EventLotteryResultsResponse> => {
  const params: any = { page, per_page: perPage };
  if (status) {
    params.status = status;
  }
  const response = await apiClient.get<EventLotteryResultsResponse>(
    `/dashboard/sites/${siteId}/events/${eventId}/lottery_results`,
    { params }
  );
  return response.data;
};

export const exportEventLotteryResults = async (
  siteId: number,
  eventId: number,
  status?: string
): Promise<EventLotteryResultsExportResponse> => {
  const params: any = {};
  if (status) {
    params.status = status;
  }
  const response = await apiClient.get<EventLotteryResultsExportResponse>(
    `/dashboard/sites/${siteId}/events/${eventId}/export_lottery_results`,
    { params }
  );
  return response.data;
};

// 当選結果の配送申請（代理実行）
export const shipLotteryResults = async (
  siteId: number,
  eventId: number,
  lotteryResultIds: number[]
): Promise<{ message: string; shipped_count: number; skipped_count: number; errors: string[] }> => {
  const response = await apiClient.post(
    `/dashboard/sites/${siteId}/events/${eventId}/ship_lottery_results`,
    { lottery_result_ids: lotteryResultIds }
  );
  return response.data;
};

// 当選結果のポイント交換（代理実行）
export const convertLotteryResults = async (
  siteId: number,
  eventId: number,
  lotteryResultIds: number[]
): Promise<{ message: string; converted_count: number; total_points: number; errors: string[] }> => {
  const response = await apiClient.post(
    `/dashboard/sites/${siteId}/events/${eventId}/convert_lottery_results`,
    { lottery_result_ids: lotteryResultIds }
  );
  return response.data;
};

// イベント配送注文
export type EventOrder = {
  id: number;
  uid: string;
  status: string;
  tracking_number: string | null;
  notes: string | null;
  shipping_fee?: number;
  shipping_request_revertible?: boolean;
  created_at: string;
  shipped_at: string | null;
  customer_email: string | null;
  customer_name: string | null;
  address: {
    postal_code: string;
    address_line1: string;
    address_line2: string | null;
    phone_number: string | null;
  } | null;
  items: { grade_name: string | null; product_name: string | null }[];
  items_count: number;
};

export type EventOrdersResponse = {
  orders: EventOrder[];
};

export const fetchEventOrders = async (
  siteId: number,
  eventId: number,
  status?: string
): Promise<EventOrdersResponse> => {
  const params: any = {};
  if (status) {
    params.status = status;
  }
  const response = await apiClient.get<EventOrdersResponse>(
    `/dashboard/sites/${siteId}/events/${eventId}/event_orders`,
    { params }
  );
  return response.data;
};

// カスタムドメイン
export interface CustomDomainData {
  custom_domain_enabled: boolean;
  custom_domain: string | null;
  status: 'none' | 'pending' | 'approved' | 'rejected';
  redirect_subdomain: boolean;
  requested_at: string | null;
  approved_at: string | null;
  subdomain: string;
  cname_target: string;
  acme_challenge_host: string | null;
  acme_challenge_target: string | null;
}

export const fetchCustomDomain = async (siteId: number): Promise<CustomDomainData> => {
  const response = await apiClient.get<CustomDomainData>(`/dashboard/sites/${siteId}/custom_domain`);
  return response.data;
};

export const requestCustomDomain = async (siteId: number, domain: string): Promise<CustomDomainData> => {
  const response = await apiClient.post<CustomDomainData>(`/dashboard/sites/${siteId}/custom_domain`, {
    custom_domain: domain
  });
  return response.data;
};

// くじ設定削除
export const deleteKujiConfig = async (
  siteId: number,
  eventId: number
): Promise<void> => {
  await apiClient.delete(`/dashboard/sites/${siteId}/events/${eventId}/kuji_config`);
};

// くじ等級一覧取得
export const fetchKujiGrades = async (
  siteId: number,
  eventId: number
): Promise<KujiGradesResponse> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/events/${eventId}/kuji_grades`);
  return response.data;
};

// くじ等級詳細取得
export const fetchKujiGrade = async (
  siteId: number,
  eventId: number,
  gradeId: number
): Promise<KujiGrade> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/events/${eventId}/kuji_grades/${gradeId}`);
  return response.data;
};

// くじ等級作成
export const createKujiGrade = async (
  siteId: number,
  eventId: number,
  params: {
    name: string;
    probability: number;
    position?: number;
    initial_stock?: number;
  }
): Promise<KujiGrade> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/events/${eventId}/kuji_grades`, {
    kuji_grade: params
  });
  return response.data;
};

// くじ等級更新
export const updateKujiGrade = async (
  siteId: number,
  eventId: number,
  gradeId: number,
  params: {
    name?: string;
    probability?: number;
    position?: number;
    initial_stock?: number;
    remaining_stock?: number;
    lottery_animation_template_id?: number | null;
    selected_animation_id?: number | null;
  }
): Promise<KujiGrade> => {
  const response = await apiClient.patch(`/dashboard/sites/${siteId}/events/${eventId}/kuji_grades/${gradeId}`, {
    kuji_grade: params
  });
  return response.data;
};

// くじ等級削除
export const deleteKujiGrade = async (
  siteId: number,
  eventId: number,
  gradeId: number
): Promise<void> => {
  await apiClient.delete(`/dashboard/sites/${siteId}/events/${eventId}/kuji_grades/${gradeId}`);
};

// くじ等級に商品追加
export const addProductsToKujiGrade = async (
  siteId: number,
  eventId: number,
  gradeId: number,
  productIds: number[]
): Promise<KujiGrade> => {
  const response = await apiClient.post(
    `/dashboard/sites/${siteId}/events/${eventId}/kuji_grades/${gradeId}/add_products`,
    { product_ids: productIds }
  );
  return response.data;
};

// くじ等級から商品削除
export const removeProductFromKujiGrade = async (
  siteId: number,
  eventId: number,
  gradeId: number,
  productId: number
): Promise<KujiGrade> => {
  const response = await apiClient.delete(
    `/dashboard/sites/${siteId}/events/${eventId}/kuji_grades/${gradeId}/remove_product`,
    { params: { product_id: productId } }
  );
  return response.data;
};

export const updateCustomDomain = async (siteId: number, data: { redirect_subdomain?: boolean }): Promise<CustomDomainData> => {
  const response = await apiClient.put<CustomDomainData>(`/dashboard/sites/${siteId}/custom_domain`, data);
  return response.data;
};

export const deleteCustomDomain = async (siteId: number): Promise<CustomDomainData> => {
  const response = await apiClient.delete<CustomDomainData>(`/dashboard/sites/${siteId}/custom_domain`);
  return response.data;
};

// ========================================
// メールテンプレート
// ========================================

export type EmailTemplateItem = {
  template_key: string;
  default_subject: string;
  subject: string | null;
  body_text: string | null;
  customized: boolean;
  variables: string[];
  enabled: boolean;
  toggleable: boolean;
};

export type EmailDesign = {
  header_color: string;
  button_color: string;
  button_text_color: string;
  show_logo: boolean;
};

export type EmailTemplateDetail = {
  template_key: string;
  default_subject: string;
  default_heading: string;
  default_body_text: string;
  subject: string | null;
  heading: string | null;
  body_text: string | null;
  customized: boolean;
  variables: string[];
  system_note: string | null;
  enabled: boolean;
  toggleable: boolean;
  email_design: EmailDesign;
};

export const fetchEmailTemplates = async (siteId: number): Promise<{ email_templates: EmailTemplateItem[] }> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/email_templates`);
  return response.data;
};

export const fetchEmailTemplate = async (siteId: number, key: string): Promise<EmailTemplateDetail> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/email_templates/${key}`);
  return response.data;
};

export const updateEmailTemplate = async (
  siteId: number,
  key: string,
  params: { subject?: string; heading?: string; body_text?: string; enabled?: boolean }
): Promise<EmailTemplateDetail> => {
  const response = await apiClient.put(`/dashboard/sites/${siteId}/email_templates/${key}`, params);
  return response.data;
};

// ========================================
// 二段階認証 (2FA)
// ========================================

export type TwoFactorSetupResponse = {
  secret: string;
  qr_svg: string;
  provisioning_uri: string;
};

export type TwoFactorStatusResponse = {
  otp_enabled: boolean;
};

export const fetch2FAStatus = async (): Promise<TwoFactorStatusResponse> => {
  const response = await apiClient.get("/dashboard/two_factor/status");
  return response.data;
};

export const setup2FA = async (): Promise<TwoFactorSetupResponse> => {
  const response = await apiClient.post("/dashboard/two_factor/setup");
  return response.data;
};

export const enable2FA = async (code: string): Promise<{ message: string; otp_enabled: boolean }> => {
  const response = await apiClient.post("/dashboard/two_factor/enable", { code });
  return response.data;
};

export const disable2FA = async (password: string): Promise<{ message: string; otp_enabled: boolean }> => {
  const response = await apiClient.delete("/dashboard/two_factor", { data: { password } });
  return response.data;
};

export const verify2FA = async (tempToken: string, code: string): Promise<{ token: string; message: string }> => {
  const response = await apiClient.post("/dashboard/sessions/verify_2fa", {
    temp_token: tempToken,
    code,
  });
  return response.data;
};

// ========================================
// クーポン
// ========================================

export type Coupon = {
  id: number;
  code: string;
  name: string;
  status: 'draft' | 'active' | 'paused' | 'completed';
  points: number;
  usage_limit: number | null;
  usage_count: number;
  total_awarded: number;
  starts_at: string | null;
  ends_at: string | null;
  new_users_only: boolean;
  new_customer_only_days: number | null;
  redemptions_count: number;
  created_at: string;
  updated_at: string;
};

export type CouponRedemption = {
  id: number;
  customer_id: number;
  customer_email: string;
  points_awarded: number;
  created_at: string;
};

export type CreateCouponParams = {
  code: string;
  name: string;
  points: number;
  usage_limit?: number | null;
  starts_at?: string | null;
  ends_at?: string | null;
  new_users_only?: boolean;
  new_customer_only_days?: number | null;
};

export type CouponDetailResponse = {
  coupon: Coupon;
  redemptions: CouponRedemption[];
  meta: PaginationMeta;
};

export const fetchCoupons = async (
  siteId: number
): Promise<{ coupons: Coupon[] }> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/coupons`);
  return response.data;
};

export const fetchCoupon = async (
  siteId: number,
  couponId: number,
  params?: { page?: number }
): Promise<CouponDetailResponse> => {
  const response = await apiClient.get(`/dashboard/sites/${siteId}/coupons/${couponId}`, { params });
  return response.data;
};

export const createCoupon = async (
  siteId: number,
  params: CreateCouponParams
): Promise<{ success: boolean; coupon: Coupon }> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/coupons`, {
    coupon: params
  });
  return response.data;
};

export const updateCoupon = async (
  siteId: number,
  couponId: number,
  params: Partial<CreateCouponParams>
): Promise<{ success: boolean; coupon: Coupon }> => {
  const response = await apiClient.put(`/dashboard/sites/${siteId}/coupons/${couponId}`, {
    coupon: params
  });
  return response.data;
};

export const deleteCoupon = async (
  siteId: number,
  couponId: number
): Promise<{ success: boolean }> => {
  const response = await apiClient.delete(`/dashboard/sites/${siteId}/coupons/${couponId}`);
  return response.data;
};

export const activateCoupon = async (
  siteId: number,
  couponId: number
): Promise<{ success: boolean; coupon: Coupon }> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/coupons/${couponId}/activate`);
  return response.data;
};

export const pauseCoupon = async (
  siteId: number,
  couponId: number
): Promise<{ success: boolean; coupon: Coupon }> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/coupons/${couponId}/pause`);
  return response.data;
};

export const completeCoupon = async (
  siteId: number,
  couponId: number
): Promise<{ success: boolean; coupon: Coupon }> => {
  const response = await apiClient.post(`/dashboard/sites/${siteId}/coupons/${couponId}/complete`);
  return response.data;
};

// ===== 活動ログ =====

export type DashboardActivityLog = {
  id: number;
  action: string;
  action_label: string;
  ip_address: string | null;
  user_agent: string | null;
  risk_score: number;
  success: boolean;
  created_at: string;
  actor: {
    id: number;
    email: string | null;
    type: string;
  } | null;
  same_ip_accounts: number;
};

export type ActivityLogStats = {
  total: number;
  today: number;
  high_risk: number;
  unique_ips: number;
};

export type ActivityLogsResponse = {
  activity_logs: DashboardActivityLog[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_count: number;
  };
  stats: ActivityLogStats;
};

export const fetchActivityLogs = async (
  siteId: number,
  params: {
    page?: number;
    ip?: string;
    customer_id?: string;
    user_agent?: string;
    action_type?: string;
    high_risk?: boolean;
  } = {}
): Promise<ActivityLogsResponse> => {
  const queryParams: Record<string, string | number | boolean> = {};
  if (params.page) queryParams.page = params.page;
  if (params.ip) queryParams.ip = params.ip;
  if (params.customer_id) queryParams.customer_id = params.customer_id;
  if (params.user_agent) queryParams.user_agent = params.user_agent;
  if (params.action_type) queryParams.action_type = params.action_type;
  if (params.high_risk) queryParams.high_risk = true;

  const response = await apiClient.get<ActivityLogsResponse>(
    `/dashboard/sites/${siteId}/activity_logs`,
    { params: queryParams }
  );
  return response.data;
};
